import posthog from 'posthog-js';

// Feature Flag: Control analytics activation
const ANALYTICS_ENABLED = import.meta.env.VITE_ANALYTICS_ENABLED === 'true';
const INTERNAL_TEST_CODE = 'Hiva_ANALYTICS_INTERNAL'; // Secret code for internal testing

// Check if analytics should be enabled
const shouldEnableAnalytics = (): boolean => {
    // 1. Check if explicitly disabled in env
    if (import.meta.env.VITE_ANALYTICS_ENABLED === 'false') {
        console.log('🔒 [Analytics] Disabled via environment variable');
        return false;
    }

    // 2. Check for internal testing mode (localStorage)
    if (typeof window !== 'undefined') {
        const internalMode = localStorage.getItem('hiva_analytics_internal');
        if (internalMode === INTERNAL_TEST_CODE) {
            console.log('🧪 [Analytics] INTERNAL TESTING MODE - Events will be tracked');
            return true;
        }
    }

    // 3. Check if enabled in production
    if (ANALYTICS_ENABLED) {
        console.log('✅ [Analytics] Enabled in production');
        return true;
    }

    // Default: disabled
    console.log('🔒 [Analytics] Disabled by default - Use internal test code to enable');
    return false;
};

// Initialize PostHog only if analytics is enabled
export const initPostHog = () => {
    if (typeof window !== 'undefined' && shouldEnableAnalytics()) {
        posthog.init(
            import.meta.env.VITE_POSTHOG_KEY || 'phc_placeholder',
            {
                api_host: 'https://app.posthog.com',
                autocapture: false,
                capture_pageview: false,
                loaded: (posthog) => {
                    if (import.meta.env.DEV) {
                        posthog.debug();
                    }
                }
            }
        );
        console.log('📊 [Analytics] PostHog initialized successfully');
    } else {
        console.log('🔒 [Analytics] PostHog NOT initialized - tracking disabled');
    }
};

// Helper functions for internal testing (accessible via browser console)
export const enableInternalAnalytics = () => {
    if (typeof window !== 'undefined') {
        localStorage.setItem('hiva_analytics_internal', INTERNAL_TEST_CODE);
        console.log('✅ [Analytics] Internal testing mode ENABLED');
        console.log('🔄 Reload the page to activate analytics tracking');
        return 'Internal analytics enabled. Please reload the page.';
    }
};

export const disableInternalAnalytics = () => {
    if (typeof window !== 'undefined') {
        localStorage.removeItem('hiva_analytics_internal');
        console.log('🔒 [Analytics] Internal testing mode DISABLED');
        console.log('🔄 Reload the page to stop analytics tracking');
        return 'Internal analytics disabled. Please reload the page.';
    }
};

export const checkAnalyticsStatus = () => {
    const enabled = shouldEnableAnalytics();
    const mode = localStorage.getItem('hiva_analytics_internal') === INTERNAL_TEST_CODE
        ? 'INTERNAL TESTING'
        : import.meta.env.VITE_ANALYTICS_ENABLED === 'true'
            ? 'PRODUCTION'
            : 'DISABLED';

    console.log(`
📊 Analytics Status:
- Enabled: ${enabled}
- Mode: ${mode}
- PostHog Key: ${import.meta.env.VITE_POSTHOG_KEY ? '✅ Configured' : '❌ Missing'}

Commands:
- enableInternalAnalytics() - Enable internal testing
- disableInternalAnalytics() - Disable internal testing
- checkAnalyticsStatus() - Show this status
    `);

    return { enabled, mode };
};

// Expose to window for console access (dev only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
    (window as any).enableInternalAnalytics = enableInternalAnalytics;
    (window as any).disableInternalAnalytics = disableInternalAnalytics;
    (window as any).checkAnalyticsStatus = checkAnalyticsStatus;
}

// Analytics Events
export const analytics = {
    // Landing Page Events
    landingView: () => {
        posthog.capture('landing_view');
    },

    heroCTAClick: () => {
        posthog.capture('hero_cta_click');
    },

    pricingView: () => {
        posthog.capture('pricing_view');
    },

    planClick: (planName: string, price: number) => {
        posthog.capture('plan_click', {
            plan: planName,
            price: price
        });
    },

    faqExpand: (question: string) => {
        posthog.capture('faq_expand', {
            question: question
        });
    },

    roiCalculatorInteraction: (photosPerMonth: number, costPerPhoto: number, savings: number) => {
        posthog.capture('roi_calculator_interaction', {
            photos_per_month: photosPerMonth,
            cost_per_photo: costPerPhoto,
            monthly_savings: savings
        });
    },

    // Auth Events
    signupStart: () => {
        posthog.capture('signup_start');
    },

    signupComplete: (userId: string, email: string) => {
        posthog.identify(userId, { email });
        posthog.capture('signup_complete');
    },

    loginStart: () => {
        posthog.capture('login_start');
    },

    loginComplete: (userId: string, email: string) => {
        posthog.identify(userId, { email });
        posthog.capture('login_complete');
    },

    loginError: (error: string) => {
        posthog.capture('login_error', {
            error: error
        });
    },

    // Studio Events
    studioEnter: (isFirstTime: boolean) => {
        posthog.capture('studio_enter', {
            first_time: isFirstTime
        });
    },

    modeSelect: (mode: string) => {
        posthog.capture('mode_select', {
            mode: mode
        });
    },

    uploadImage: (type: 'model' | 'clothing' | 'product' | 'variation') => {
        posthog.capture('upload_image', {
            type: type
        });
    },

    promptSubmit: (prompt: string, mode: string) => {
        posthog.capture('prompt_submit', {
            prompt_length: prompt.length,
            mode: mode
        });
    },

    generationStart: (mode: string, numberOfImages: number) => {
        posthog.capture('generation_start', {
            mode: mode,
            number_of_images: numberOfImages
        });
    },

    generationSuccess: (mode: string, timeElapsed: number) => {
        posthog.capture('generation_success', {
            mode: mode,
            time_elapsed_seconds: timeElapsed
        });
    },

    generationError: (error: string, mode: string) => {
        posthog.capture('generation_error', {
            error: error,
            mode: mode
        });
    },

    upscaleClick: () => {
        posthog.capture('upscale_click');
    },

    videoClick: () => {
        posthog.capture('video_click');
    },

    downloadImage: () => {
        posthog.capture('download_image');
    },

    // Monetization Events
    pricingModalOpen: (trigger: 'manual' | 'no_credits' | 'upsell') => {
        posthog.capture('pricing_modal_open', {
            trigger: trigger
        });
    },

    planSelect: (planName: string, price: number, planType: string) => {
        posthog.capture('plan_select', {
            plan: planName,
            price: price,
            plan_type: planType
        });
    },

    checkoutStart: (planName: string, price: number) => {
        posthog.capture('checkout_start', {
            plan: planName,
            price: price
        });
    },

    purchaseComplete: (planName: string, price: number, credits: number) => {
        posthog.capture('purchase_complete', {
            plan: planName,
            price: price,
            credits: credits,
            value: price // For revenue tracking
        });
    },

    upsellShown: (generationCount: number) => {
        posthog.capture('upsell_shown', {
            generation_count: generationCount
        });
    },

    upsellAccepted: () => {
        posthog.capture('upsell_accepted');
    },

    upsellDismissed: () => {
        posthog.capture('upsell_dismissed');
    },

    // Onboarding Events
    onboardingShown: () => {
        posthog.capture('onboarding_shown');
    },

    onboardingSkipped: () => {
        posthog.capture('onboarding_skipped');
    },

    onboardingTourStarted: () => {
        posthog.capture('onboarding_tour_started');
    },

    // Churn Events
    creditsDepleted: () => {
        posthog.capture('credits_depleted');
    },

    thirtyDaysInactive: () => {
        posthog.capture('30_days_inactive');
    },

    // User Properties
    setUserProperties: (userId: string, properties: Record<string, any>) => {
        posthog.identify(userId, properties);
    },

    // Generic Track (for flexibility)
    track: (eventName: string, properties?: Record<string, any>) => {
        posthog.capture(eventName, properties);
    }
};

export { posthog };
